import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import {
  getWeeklyProfitableProducts,
  getMonthlyProfitableProducts,
} from "../../slices/thunks";
import { createSelector } from "reselect";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import ReactApexChart from "react-apexcharts";
import {
  DASHBOARD_DATE_FILTER_PERIODS,
  getDashboardDateRange,
  shouldUseMonthlyEndpoint,
} from "./dateFilterUtils";
import { toChartItems, toChartLabel, toChartNumber } from "./dashboardChartUtils";

const CHANNELS = [
  { key: "fbaCount", label: "FBA", color: "#40bb82" },
  { key: "fbmCount", label: "FBM", color: "#ffca5b" },
  { key: "ebayCount", label: "eBay", color: "#ee6352" },
  { key: "walmartCount", label: "Walmart", color: "#3680fb" },
  { key: "otherCount", label: "Other", color: "#f672a7" },
  { key: "onlineStoreCount", label: "Online Store", color: "#0dcaf0" },
  { key: "liquidationsCount", label: "Liquidations", color: "#6f42c1" },
  { key: "totalCount", label: "Total", color: "#343a40" },
];

const WeeklyProfitsByChannel = ({ dateFilter = null }) => {
  const dispatch = useDispatch();
  const dateFrom = useRef(null);
  const dateTo = useRef(null);
  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const isGlobalDateFilter = Boolean(dateFilter);

  const selectDashboardData = createSelector(
    (state) => state.Dashboard,
    (dashboard) => dashboard
  );
  const { weeklyProfitableProducts } = useSelector(selectDashboardData);

  const [chartState, setChartState] = useState({
    series: [],
    options: {
      chart: {
        height: 350,
        type: "bar",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          dataLabels: {
            position: "top",
          },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -20,
        style: {
          fontSize: "12px",
          colors: ["#304758"],
        },
      },
      xaxis: {
        categories: [],
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        crosshairs: {
          fill: {
            type: "gradient",
            gradient: {
              colorFrom: "#D8E3F0",
              colorTo: "#BED1E6",
              stops: [0, 100],
              opacityFrom: 0.4,
              opacityTo: 0.5,
            },
          },
        },
        tooltip: {
          enabled: true,
        },
      },
      yaxis: {
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          show: false,
        },
      },
    },
  });

  const getData = () => {
    const data = {
      dateFrom:
        dateFrom.current != null
          ? moment(dateFrom.current).format("YYYY-MM-DD")
          : null,
      dateTo:
        dateTo.current != null
          ? moment(dateTo.current).format("YYYY-MM-DD")
          : null,
    };
    if (isGlobalDateFilter && shouldUseMonthlyEndpoint(dateFilter)) {
      dispatch(getMonthlyProfitableProducts(data));
    } else {
      dispatch(getWeeklyProfitableProducts(data));
    }
  };

  const periodLabel =
    isGlobalDateFilter && shouldUseMonthlyEndpoint(dateFilter)
      ? "Monthly"
      : "Weekly";

  useEffect(() => {
    if (weeklyProfitableProducts) {
      const chartItems = toChartItems(weeklyProfitableProducts);

      const seriesData = CHANNELS.map((channel) => ({
        name: channel.label,
        type: "bar",
        data: chartItems.map((item) => toChartNumber(item?.[channel.key])),
        color: channel.color,
      }));

      setChartState((prevState) => ({
        ...prevState,
        series: seriesData,
        options: {
          ...prevState.options,
          xaxis: {
            ...prevState.options.xaxis,
            categories: chartItems.map((item) => toChartLabel(item?.label)),
          },
          plotOptions: {
            bar: {
              dataLabels: {
                position: "top",
              },
            },
          },
        },
      }));
    }
  }, [weeklyProfitableProducts]);

  useEffect(() => {
    if (!isGlobalDateFilter) {
      return;
    }

    const range = getDashboardDateRange(
      dateFilter.period,
      dateFilter.customDateFrom,
      dateFilter.customDateTo
    );

    dateFrom.current = range.dateFrom;
    dateTo.current = range.dateTo;

    if (
      dateFilter.period !== DASHBOARD_DATE_FILTER_PERIODS.CUSTOM ||
      range.dateFrom ||
      range.dateTo
    ) {
      getData();
    }
  }, [
    dateFilter?.customDateFrom,
    dateFilter?.customDateTo,
    dateFilter?.period,
    isGlobalDateFilter,
  ]);

  useEffect(() => {
    if (isGlobalDateFilter) {
      return;
    }

    switch (selectedPeriod) {
      case "7 days":
        const date = new Date();
        const sevenDaysAgo = new Date(date);
        sevenDaysAgo.setDate(date.getDate() - 7);
        dateFrom.current = sevenDaysAgo;
        dateTo.current = new Date();
        break;
      case "30 days":
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(new Date().getDate() - 30);
        dateFrom.current = thirtyDaysAgo;
        dateTo.current = new Date();
        break;
      case "all":
        dateFrom.current = null;
        dateTo.current = null;
        break;
      case "custom":
        break;
      default:
        dateFrom.current = null;
        dateTo.current = null;
        break;
    }
    if (selectedPeriod !== "custom") {
      getData();
    }
  }, [isGlobalDateFilter, selectedPeriod]);

  return (
    <React.Fragment>
      <Card>
        <CardHeader className="border-0 align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">
            {periodLabel} Profitable Products by Channel
          </h4>
          {!isGlobalDateFilter && (
            <div className="d-flex gap-1">
              <button
                type="button"
                className={`btn btn-soft-${
                  selectedPeriod === "all" ? "success" : "secondary"
                } btn-sm shadow-none`}
                onClick={() => {
                  setSelectedPeriod("all");
                }}
              >
                ALL
              </button>
              <button
                type="button"
                className={`btn btn-soft-${
                  selectedPeriod === "7 days" ? "success" : "secondary"
                } btn-sm shadow-none`}
                onClick={() => {
                  setSelectedPeriod("7 days");
                }}
              >
                7 Days
              </button>
              <button
                type="button"
                className={`btn btn-soft-${
                  selectedPeriod === "30 days" ? "success" : "secondary"
                } btn-sm shadow-none`}
                onClick={() => {
                  setSelectedPeriod("30 days");
                }}
              >
                30 Days
              </button>
              <button
                type="button"
                className={`btn btn-soft-${
                  selectedPeriod === "custom" ? "success" : "secondary"
                } btn-sm shadow-none`}
                onClick={() => {
                  setSelectedPeriod("custom");
                }}
              >
                Custom
              </button>
              {selectedPeriod === "custom" && (
                <div className="d-flex align-items-center gap-2 mt-2">
                  <Flatpickr
                    className="form-control"
                    id="datepicker-profits-from"
                    placeholder="Select a date from"
                    onChange={(selectedDates) => {
                      dateFrom.current = selectedDates[0];
                      getData();
                    }}
                    options={{
                      maxDate: dateTo.current,
                      mode: "single",
                      dateFormat: "m-d-Y",
                    }}
                  />
                  <span>to</span>
                  <Flatpickr
                    className="form-control"
                    id="datepicker-profits-to"
                    placeholder="Select a date to"
                    onChange={(selectedDates) => {
                      dateTo.current = selectedDates[0];
                      getData();
                    }}
                    options={{
                      mode: "single",
                      minDate: dateFrom.current,
                      dateFormat: "m-d-Y",
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </CardHeader>
        <CardBody className="p-0 pb-2">
          <div className="w-100">
            <div dir="ltr">
              {weeklyProfitableProducts ? (
                <ReactApexChart
                  options={chartState.options}
                  series={chartState.series}
                  type="bar"
                  height={350}
                />
              ) : (
                <div className="text-center p-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default WeeklyProfitsByChannel;
